import React, { useState, useEffect } from 'react';
import { coreAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useOrganization } from '../../context/OrganizationContext';
import './Public.css';
import './Contact.css';

const Contact = () => {
  const { success, error: showError } = useToast();
  const { organization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [officeInfo, setOfficeInfo] = useState(null);
  const [formData, setFormData] = useState({
    organization: organization?.id || 1,
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchOfficeInfo();
  }, []);

  const fetchOfficeInfo = async () => {
    try {
      const response = await coreAPI.getOfficeInfo();
      setOfficeInfo(response.data);
    } catch (err) {
      console.error('Failed to fetch office info:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = '請輸入姓名';
    if (!formData.phone.trim()) {
      newErrors.phone = '請輸入電話號碼';
    } else if (!/^[0-9]{8}$/.test(formData.phone.trim())) {
      newErrors.phone = '請輸入有效的8位數字電話號碼';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '請輸入有效的電郵地址';
    }
    if (!formData.message.trim()) newErrors.message = '請輸入訊息內容';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      await coreAPI.sendMessage(formData);
      success('訊息已成功提交，我們會盡快回覆您');
      setFormData({
        organization: organization?.id || 1,
        name: '',
        phone: '',
        email: '',
        subject: '',
        message: ''
      });
      setErrors({});
    } catch (err) {
      showError('提交失敗，請稍後再試');
      console.error('Error sending message:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page public-page">
      <div className="page-header">
        <div className="container">
          <h1>聯絡我們</h1>
          <p className="page-subtitle">歡迎與我們聯絡，我們會盡快回覆您的查詢</p>
        </div>
      </div>

      <div className="container">
        <div className="contact-container">
          <div className="contact-info card">
            <h2>辦公室資訊</h2>
            {officeInfo && (
              <>
                <div className="info-item">
                  <i className="icon">📍</i>
                  <div>
                    <strong>地址</strong>
                    <p>{officeInfo.address}</p>
                  </div>
                </div>
                <div className="info-item">
                  <i className="icon">📞</i>
                  <div>
                    <strong>電話</strong>
                    {Array.isArray(officeInfo.phone) ? (
                      officeInfo.phone.map((phone, index) => (
                        <p key={index}>{phone}</p>
                      ))
                    ) : (
                      <p>{officeInfo.phone}</p>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <i className="icon">📧</i>
                  <div>
                    <strong>電郵</strong>
                    <p>{officeInfo.email}</p>
                  </div>
                </div>
                <div className="info-item">
                  <i className="icon">🕐</i>
                  <div>
                    <strong>辦公時間</strong>
                    <p>{officeInfo.office_hours}</p>
                  </div>
                </div>
              </>
            )}
          </div>

          <form className="contact-form card" onSubmit={handleSubmit}>
            <h2>發送訊息</h2>
            
            <div className="form-group">
              <label>姓名 *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="請輸入您的姓名"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>電話號碼 *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="12345678"
                  maxLength="8"
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label>電郵地址</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="example@email.com"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>主旨</label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="請輸入主旨（選填）"
              />
            </div>

            <div className="form-group">
              <label>訊息內容 *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="6"
                placeholder="請輸入您的訊息..."
                className={errors.message ? 'error' : ''}
              ></textarea>
              {errors.message && <span className="error-text">{errors.message}</span>}
            </div>

            <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
              {loading ? '提交中...' : '提交訊息'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
